/**
 * VaultPage - The Vault: Personal Comic Collection Manager
 * USER-CON-001 Phase 2 - Three-Pane Layout
 *
 * Features:
 * - Three-pane layout (sidebar, grid, detail)
 * - Smart Sets and Boxes navigation
 * - Grid/List/Run/Matrix view of collection
 * - Full keyboard navigation (J/K/E/S/B/G)
 * - Bulk actions
 * - Command Palette (Cmd+K)
 * - ARIA accessibility
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Archive, Plus, Upload, X, Clock,
  Package, Command, Settings, Map, Grid as GridIcon,
  Download, Search, DollarSign
} from 'lucide-react';
import VaultSidebar from './VaultSidebar';
import VaultGrid from './VaultGrid';
import VaultDetailPane from './VaultDetailPane';
import RunView from './views/RunView';
import VariantMatrixView from './views/VariantMatrixView';
import CommandPalette from './CommandPalette';
import GapAnalysisPanel from './GapAnalysisPanel';
import GapAlertBanner from './GapAlertBanner';
import ImportHistoryPanel from './ImportHistoryPanel';
import ExportFilterDialog from './ExportFilterDialog';
import ImportModal from './ImportModal';
import AddToVault from './AddToVault';
// VAULT-S2b OP-2 RELEASED: the full valuation dashboard is the 'Value' panel the stats badge opens.
import CollectionValueDashboard from './CollectionValueDashboard';
import useVaultKeyboard from '../../hooks/useVaultKeyboard';
import {
  getVaultItems,
  getVaultStats,
  removeVaultItem,
  updateVaultItem,
  getSmartSetItems,
  getBoxItems,
  addToSellQueue,
  assignItemsToBox,
  createBox,
  createSmartSet,
  exportCollection,
} from '../../services/vaultApi';

// V-IMPORT: the CLZ/CSV ImportModal now lives in ./ImportModal.jsx (one state machine for the
// desktop modal and the mobile sheet). V-ADD: the free-text AddItemModal is gone — the Add modal
// hosts ./AddToVault.jsx, the same tab body QuickAdd renders on mobile.

// Create Box Modal
function CreateBoxModal({ isOpen, onClose, onCreate }) {
  const [formData, setFormData] = useState({
    name: '',
    box_type: 'short',
    capacity: 200,
    location: '',
    description: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const result = await createBox(formData);
      onCreate(result);
      onClose();
      setFormData({ name: '', box_type: 'short', capacity: 200, location: '', description: '' });
    } catch (error) {
      console.error('Failed to create box:', error);
    }
    setIsLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">New Box</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              placeholder="e.g., Silver Age Marvel"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Box Type</label>
              <select
                value={formData.box_type}
                onChange={(e) => setFormData({ ...formData, box_type: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              >
                <option value="short">Short Box</option>
                <option value="long">Long Box</option>
                <option value="magazine">Magazine Box</option>
                <option value="drawer">Drawer</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Capacity</label>
              <input
                type="number"
                value={formData.capacity}
                onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-1">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              placeholder="e.g., Closet Shelf 2"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || !formData.name}
            className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-zinc-700 text-white font-medium rounded-lg transition-colors"
          >
            {isLoading ? 'Creating...' : 'Create Box'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Create Smart Set Modal
function CreateSmartSetModal({ isOpen, onClose, onCreate }) {
  const [formData, setFormData] = useState({
    name: '',
    icon: 'star',
    color: '#F97316',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Simple default query - user can edit later
      const result = await createSmartSet({
        ...formData,
        query: { logic: 'and', conditions: [] },
      });
      onCreate(result);
      onClose();
      setFormData({ name: '', icon: 'star', color: '#F97316' });
    } catch (error) {
      console.error('Failed to create smart set:', error);
    }
    setIsLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">New Smart Set</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              placeholder="e.g., High Value Slabs"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Icon</label>
              <select
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              >
                <option value="star">Star</option>
                <option value="heart">Heart</option>
                <option value="trophy">Trophy</option>
                <option value="dollar-sign">Dollar</option>
                <option value="pen">Pen</option>
                <option value="copy">Copy</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Color</label>
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-full h-10 px-1 py-1 bg-zinc-800 border border-zinc-700 rounded-lg cursor-pointer"
              />
            </div>
          </div>

          <p className="text-xs text-zinc-500">
            After creating, you can edit the smart set to define filter conditions.
          </p>

          <button
            type="submit"
            disabled={isLoading || !formData.name}
            className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-zinc-700 text-white font-medium rounded-lg transition-colors"
          >
            {isLoading ? 'Creating...' : 'Create Smart Set'}
          </button>
        </form>
      </div>
    </div>
  );
}

// View modes
const VIEW_MODES = ['grid', 'list', 'run', 'matrix'];

// Main Vault Page
/**
 * @param {object} props
 * @param {() => void} [props.onBack] DEPRECATED, UNUSED (V-NAV-EGRESS). The Vault is the
 *   authenticated HOME, so the header back arrow that called this -- an unlabelled icon that
 *   dropped the user onto the empty store shell -- was removed. The prop is still accepted so
 *   the App.jsx mount keeps compiling; it is never read. Egress from the overlay is the
 *   sidebar's Supply Lab rail (V-NAV-RAIL) and the MaestroNav wordmark on other pages.
 * @param {React.ReactNode} [props.accountMenu] V-NAV-ACCOUNT: the shared account dropdown, built
 *   by App.jsx (which owns the user and every destination) and rendered here as the RIGHTMOST
 *   toolbar control. The overlay is `z-50 fixed inset-0` and therefore COVERS storeApp's header,
 *   so without this the vault was the one authed surface with no account menu and no way to sign
 *   out. Passed as a node rather than as user+handlers so the vault needs to know nothing about
 *   auth, modals or routing.
 */
export default function VaultPage({ onBack, accountMenu = null }) {
  // View state
  const [activeView, setActiveView] = useState('all'); // 'all', 'smart-set', 'box', 'sell-queue'
  const [activeSmartSet, setActiveSmartSet] = useState(null);
  const [activeBox, setActiveBox] = useState(null);

  // Data state
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // UI state
  const [viewMode, setViewMode] = useState('grid'); // grid, list, run, matrix
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at:desc');
  const [activeItem, setActiveItem] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showDetailPane, setShowDetailPane] = useState(true);
  const [showCommandPalette, setShowCommandPalette] = useState(false);

  // Refs
  const searchInputRef = useRef(null);

  // Modals
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCreateBoxModal, setShowCreateBoxModal] = useState(false);
  const [showCreateSmartSetModal, setShowCreateSmartSetModal] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showExportFilter, setShowExportFilter] = useState(false);
  const [showImportHistory, setShowImportHistory] = useState(false);
  // V-ADD: which AddToVault tab the modal opens on ('search' | 'scan' | 'bulk' | 'manual' | 'capture')
  const [addInitialTab, setAddInitialTab] = useState('search');
  const [showGapAnalysis, setShowGapAnalysis] = useState(false);
  // OP-2: the 'Value' panel (full CollectionValueDashboard), toggled from the header stats badge
  const [showValuePanel, setShowValuePanel] = useState(false);

  // Load data based on active view
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      let result;
      const [field, order] = sortBy.split(':');

      if (activeView === 'smart-set' && activeSmartSet) {
        result = await getSmartSetItems(activeSmartSet.id, {
          page: currentPage,
          page_size: 24,
        });
      } else if (activeView === 'box' && activeBox) {
        result = await getBoxItems(activeBox.id, {
          page: currentPage,
          page_size: 24,
        });
      } else {
        result = await getVaultItems({
          page: currentPage,
          per_page: 24,
          sort: sortBy,
        });
      }

      setItems(result.items || []);
      setTotalCount(result.total || 0);
      setTotalPages(result.total_pages || Math.ceil((result.total || 0) / 24));

      // Load stats for all view
      if (activeView === 'all') {
        const statsResult = await getVaultStats();
        setStats(statsResult || {});
      }
    } catch (error) {
      console.error('Failed to load vault data:', error);
    }
    setIsLoading(false);
  }, [activeView, activeSmartSet, activeBox, currentPage, sortBy]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset page when view changes
  useEffect(() => {
    setCurrentPage(1);
    setSelectedItems([]);
    setActiveItem(null);
  }, [activeView, activeSmartSet, activeBox]);

  // Handle sidebar navigation
  const handleViewChange = (view) => {
    setActiveView(view);
    setActiveSmartSet(null);
    setActiveBox(null);
  };

  const handleSmartSetSelect = (set) => {
    setActiveView('smart-set');
    setActiveSmartSet(set);
    setActiveBox(null);
    setShowGapAnalysis(false);
  };

  const handleBoxSelect = (box) => {
    setActiveView('box');
    setActiveBox(box);
    setActiveSmartSet(null);
  };

  // Handle item interactions
  const handleItemClick = (item) => {
    setActiveItem(item);
    setShowDetailPane(true);
  };

  // ⛔ VAULT-S4 V-SMART-FE-REQUESTS: the variant-matrix want-list caller is GONE. It posted
  // `[issue.issue_number]` -- the string "300" -- to a route keyed on comic_issue_id, so every
  // click failed behind a green toast. The matrix payload (VariantMatrixView.seriesList, built
  // from owned vault items) carries NO comic_issue_id for a variant the user does not own, and
  // the owned copy's id is not a gap. The cell's button is disabled with the reason
  // (VariantMatrixView.WANT_LIST_UNAVAILABLE); Gap Analysis is the want-list surface.

  const handleItemDoubleClick = (item) => {
    // Could open full edit modal
    console.log('Double-click:', item);
  };

  // Bulk actions
  const handleBulkAction = async (action, itemIds) => {
    try {
      if (action === 'sell-queue') {
        await addToSellQueue(itemIds);
        loadData();
      } else if (action === 'assign-box') {
        // Would show box selection modal
        console.log('Assign to box:', itemIds);
      } else if (action === 'delete') {
        if (confirm(`Delete ${itemIds.length} items from your vault?`)) {
          for (const id of itemIds) {
            await removeVaultItem(id);
          }
          setSelectedItems([]);
          loadData();
        }
      }
    } catch (error) {
      console.error('Bulk action failed:', error);
    }
  };

  // Handle keyboard navigation
  const handleKeyboardNavigate = useCallback((index) => {
    setActiveIndex(index);
    if (items[index]) {
      setActiveItem(items[index]);
    }
  }, [items]);

  // Handle keyboard selection
  const handleKeyboardSelect = useCallback((action, itemId) => {
    if (action === 'all') {
      setSelectedItems(items.map(i => i.id));
    } else if (action === 'toggle' && itemId) {
      setSelectedItems(prev =>
        prev.includes(itemId)
          ? prev.filter(id => id !== itemId)
          : [...prev, itemId]
      );
    }
  }, [items]);

  // Handle keyboard view mode changes
  const handleKeyboardViewChange = useCallback((direction) => {
    if (typeof direction === 'string' && VIEW_MODES.includes(direction)) {
      setViewMode(direction);
    } else {
      const currentIdx = VIEW_MODES.indexOf(viewMode);
      if (direction === 'next') {
        const nextIdx = (currentIdx + 1) % VIEW_MODES.length;
        setViewMode(VIEW_MODES[nextIdx]);
      } else if (direction === 'prev') {
        const prevIdx = (currentIdx - 1 + VIEW_MODES.length) % VIEW_MODES.length;
        setViewMode(VIEW_MODES[prevIdx]);
      }
    }
  }, [viewMode]);

  // Handle keyboard actions
  const handleKeyboardAction = useCallback(async (action, target) => {
    switch (action) {
      case 'edit':
        if (target) {
          setActiveItem(target);
          setShowDetailPane(true);
        }
        break;
      case 'sell-queue':
        if (Array.isArray(target) && target.length > 0) {
          await addToSellQueue(target);
          loadData();
        }
        break;
      case 'assign-box':
        // Future: show box selector modal
        console.log('Assign to box:', target);
        break;
      case 'toggle-wanted':
        if (target) {
          await updateVaultItem(target.id, { is_wanted: !target.is_wanted });
          loadData();
        }
        break;
      case 'toggle-read':
        if (target) {
          await updateVaultItem(target.id, { is_read: !target.is_read });
          loadData();
        }
        break;
      case 'delete':
        if (Array.isArray(target) && target.length > 0) {
          if (confirm(`Delete ${target.length} item(s) from your vault?`)) {
            for (const id of target) {
              await removeVaultItem(id);
            }
            setSelectedItems([]);
            setActiveItem(null);
            loadData();
          }
        }
        break;
      case 'new-item':
        setAddInitialTab('search');
        setShowAddModal(true);
        break;
      case 'import':
        setShowImportModal(true);
        break;
      case 'open-detail':
        if (target) {
          setActiveItem(target);
          setShowDetailPane(true);
        }
        break;
      default:
        break;
    }
  }, [loadData]);

  // Handle keyboard close (Escape)
  const handleKeyboardClose = useCallback(() => {
    if (showCommandPalette) {
      setShowCommandPalette(false);
    } else if (selectedItems.length > 0) {
      setSelectedItems([]);
    } else if (activeItem) {
      setActiveItem(null);
      setShowDetailPane(false);
    }
  }, [showCommandPalette, selectedItems, activeItem]);

  // Command Palette action handler
  const handleCommandPaletteAction = useCallback(async (action) => {
    // 'modal:add-item:scan' — the third segment is an AddToVault tab hint. The palette's
    // "Scan Barcode" / "Bulk Barcode Import" entries are add-item WITH a hint now; the old
    // nav:scan-barcode did `window.location.href = '/maestro/vault/add?tab=scan'` — a full page
    // reload out of the Vault, and QuickAdd never read the param.
    const [category, command, hint] = action.split(':');

    switch (category) {
      case 'view':
        setViewMode(command);
        break;
      case 'modal':
        if (command === 'add-item') {
          setAddInitialTab(hint || 'search');
          setShowAddModal(true);
        }
        else if (command === 'import') setShowImportModal(true);
        else if (command === 'import-history') setShowImportHistory(true);
        else if (command === 'export-filter') setShowExportFilter(true);
        else if (command === 'create-box') setShowCreateBoxModal(true);
        else if (command === 'create-smart-set') setShowCreateSmartSetModal(true);
        break;
      case 'export':
        try {
          // V-EXPORT-FE: exportCollection returns the file and the SERVER's filename.
          const { blob, filename } = await exportCollection({ format: command });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        } catch (error) {
          console.error('Export failed:', error);
        }
        break;
      case 'filter':
        // Future: apply smart set filters
        console.log('Apply filter:', command);
        break;
      case 'nav':
        if (command === 'boxes') handleViewChange('all');
        break;
      case 'selection':
        if (command === 'edit' && activeItem) {
          setShowDetailPane(true);
        } else if (command === 'sell' && selectedItems.length > 0) {
          await addToSellQueue(selectedItems);
          loadData();
        } else if (command === 'delete' && selectedItems.length > 0) {
          handleKeyboardAction('delete', selectedItems);
        }
        break;
      default:
        break;
    }
  }, [activeItem, selectedItems, loadData, handleKeyboardAction, handleViewChange]);

  // Use keyboard navigation hook
  useVaultKeyboard({
    items,
    activeIndex,
    selectedItems,
    onNavigate: handleKeyboardNavigate,
    onSelect: handleKeyboardSelect,
    onAction: handleKeyboardAction,
    onViewChange: handleKeyboardViewChange,
    onOpenCommandPalette: () => setShowCommandPalette(true),
    onFocusSearch: () => searchInputRef.current?.focus(),
    onClose: handleKeyboardClose,
    enabled: !showCommandPalette && !showAddModal && !showImportModal,
  });

  // Get view title
  const getViewTitle = () => {
    if (activeView === 'smart-set' && activeSmartSet) {
      return activeSmartSet.name;
    }
    if (activeView === 'box' && activeBox) {
      return activeBox.name;
    }
    return 'All Items';
  };

  return (
    <div className="h-screen bg-zinc-950 flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* V-NAV-EGRESS: no back arrow -- the Vault is home (see the onBack JSDoc). */}
          <div className="flex items-center gap-3">
            <Archive className="w-6 h-6 text-orange-500" />
            <h1 className="font-comic text-xl text-white">
              The <span className="text-orange-500">Vault</span>
            </h1>
          </div>
          <span className="text-zinc-500 text-sm hidden sm:inline">
            / {getViewTitle()}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Stats badge -- OP-2: opens the 'Value' panel (full valuation dashboard) */}
          {stats.total_items > 0 && (
            <button
              type="button"
              onClick={() => setShowValuePanel((open) => !open)}
              className={`hidden md:flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs transition-colors ${
                showValuePanel
                  ? 'bg-green-500/10 text-green-400 border border-green-500/30'
                  : 'text-zinc-500 hover:text-white hover:bg-zinc-800'
              }`}
              title="Collection value"
              aria-expanded={showValuePanel}
              aria-controls="vault-value-panel"
              data-testid="vault-stats-badge"
            >
              <DollarSign className="w-3.5 h-3.5" />
              <span>
                {stats.total_items} items
                {stats.graded_items > 0 && ` (${stats.graded_items} graded)`}
              </span>
            </button>
          )}

          {/* Gap Analysis toggle (only when viewing a smart set) */}
          {activeView === 'smart-set' && activeSmartSet && (
            <button
              onClick={() => {
                setShowGapAnalysis(!showGapAnalysis);
                if (!showGapAnalysis) setShowDetailPane(false);
              }}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm ${
                showGapAnalysis
                  ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                  : 'bg-zinc-800 hover:bg-zinc-700 text-white'
              }`}
            >
              <Search className="w-4 h-4" />
              <span className="hidden sm:inline">Gaps</span>
            </button>
          )}

          {/* Command palette trigger */}
          <button
            onClick={() => setShowCommandPalette(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors text-sm"
            title="Command Palette (Cmd+K)"
          >
            <Command className="w-4 h-4" />
            <span className="hidden sm:inline">
              <kbd className="text-xs bg-zinc-700 px-1 rounded">⌘K</kbd>
            </span>
          </button>

          {/* Export dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors text-sm"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
            {showExportMenu && (
              <div className="absolute right-0 top-full mt-1 w-40 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50 overflow-hidden">
                <button
                  onClick={async () => {
                    setShowExportMenu(false);
                    await handleCommandPaletteAction('export:csv');
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-800"
                >
                  Export CSV
                </button>
                <button
                  onClick={async () => {
                    setShowExportMenu(false);
                    await handleCommandPaletteAction('export:json');
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-800"
                >
                  Export JSON
                </button>
                <button
                  onClick={async () => {
                    setShowExportMenu(false);
                    await handleCommandPaletteAction('export:ebay');
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-800"
                >
                  Export for eBay
                </button>
                <button
                  onClick={async () => {
                    setShowExportMenu(false);
                    await handleCommandPaletteAction('export:clz');
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-800"
                >
                  Export CLZ Format
                </button>
                <button
                  onClick={() => {
                    setShowExportMenu(false);
                    setShowExportFilter(true);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-orange-400 hover:bg-zinc-800 border-t border-zinc-700"
                >
                  Export with Filters...
                </button>
              </div>
            )}
          </div>

          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors text-sm"
          >
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">Import</span>
          </button>
          <button
            onClick={() => setShowImportHistory(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white rounded-lg transition-colors text-sm"
            title="Import History"
          >
            <Clock className="w-4 h-4" />
          </button>
          <button
            onClick={() => { setAddInitialTab('search'); setShowAddModal(true); }}
            className="flex items-center gap-2 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors text-sm"
            data-testid="vault-add-button"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add</span>
          </button>
          {/* V-NAV-ACCOUNT: LAST in the row, matching every other surface's top bar. */}
          {accountMenu}
        </div>
      </div>

      {/* Gap Alert Banner */}
      <GapAlertBanner onViewGaps={() => {
        if (activeSmartSet) setShowGapAnalysis(true);
      }} />

      {/* Three-Pane Layout */}
      <div className="flex-1 flex min-h-0">
        {/* Left Sidebar */}
        <VaultSidebar
          activeView={activeView}
          activeSmartSet={activeSmartSet}
          activeBox={activeBox}
          onViewChange={handleViewChange}
          onSmartSetSelect={handleSmartSetSelect}
          onBoxSelect={handleBoxSelect}
          onCreateBox={() => setShowCreateBoxModal(true)}
          onCreateSmartSet={() => setShowCreateSmartSetModal(true)}
        />

        {/* Center Content - View dependent */}
        {viewMode === 'run' ? (
          <RunView
            items={items}
            isLoading={isLoading}
            onItemClick={handleItemClick}
          />
        ) : viewMode === 'matrix' ? (
          <VariantMatrixView
            items={items}
            isLoading={isLoading}
            onCellClick={handleItemClick}
          />
        ) : (
          <VaultGrid
            items={items}
            isLoading={isLoading}
            activeItem={activeItem}
            activeIndex={activeIndex}
            selectedItems={selectedItems}
            onItemClick={handleItemClick}
            onItemDoubleClick={handleItemDoubleClick}
            onSelectionChange={setSelectedItems}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            sortBy={sortBy}
            onSortChange={setSortBy}
            onRefresh={loadData}
            onBulkAction={handleBulkAction}
            totalCount={totalCount}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            searchInputRef={searchInputRef}
          />
        )}

        {/* Right Pane - Detail or Gap Analysis */}
        {showGapAnalysis && activeSmartSet ? (
          <GapAnalysisPanel
            smartSet={activeSmartSet}
            onClose={() => setShowGapAnalysis(false)}
          />
        ) : showDetailPane ? (
          <VaultDetailPane
            item={activeItem}
            onClose={() => {
              setActiveItem(null);
              setShowDetailPane(false);
            }}
            onUpdate={loadData}
            onDelete={(id) => {
              setItems(items.filter(i => i.id !== id));
              setActiveItem(null);
            }}
          />
        ) : null}
      </div>

      {/* Modals */}
      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={() => {
          setShowImportModal(false);
          loadData();
        }}
      />
      {/* V-ADD: one add surface. Search is the default tab; the palette passes a tab hint. Capture
          hands off to the grading route via window.location.assign — VaultPage stays router-hook-free. */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="add-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Add to Vault</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-zinc-400 hover:text-white"
                aria-label="Close add"
                data-testid="add-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <AddToVault
              initialTab={addInitialTab}
              onAdded={() => loadData()}
              onClose={() => setShowAddModal(false)}
              onCapture={() => window.location.assign('/maestro/grade/upload')}
            />
          </div>
        </div>
      )}
      <CreateBoxModal
        isOpen={showCreateBoxModal}
        onClose={() => setShowCreateBoxModal(false)}
        onCreate={() => loadData()}
      />
      <CreateSmartSetModal
        isOpen={showCreateSmartSetModal}
        onClose={() => setShowCreateSmartSetModal(false)}
        onCreate={() => loadData()}
      />

      {/* Phase 2: Import History Panel */}
      <ImportHistoryPanel
        isOpen={showImportHistory}
        onClose={() => setShowImportHistory(false)}
      />

      {/* Phase 2: Export Filter Dialog */}
      <ExportFilterDialog
        isOpen={showExportFilter}
        onClose={() => setShowExportFilter(false)}
      />

      {/* VAULT-S2b OP-2 RELEASED: the 'Value' panel -- the full CollectionValueDashboard
          (GET /vault/valuation, server-derived ladder totals). Mounted only while open, so
          every open is a fresh read. */}
      {showValuePanel && (
        <div className="fixed inset-0 z-40 flex justify-end" data-testid="vault-value-panel-overlay">
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label="Close value panel"
            onClick={() => setShowValuePanel(false)}
            data-testid="vault-value-panel-backdrop"
          />
          <aside
            id="vault-value-panel"
            role="dialog"
            aria-label="Collection value"
            className="relative w-full max-w-3xl h-full bg-zinc-950 border-l border-zinc-800 overflow-y-auto shadow-2xl"
            data-testid="vault-value-panel"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-zinc-950/95 backdrop-blur border-b border-zinc-800">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-400" />
                Value
              </h2>
              <button
                type="button"
                onClick={() => setShowValuePanel(false)}
                className="text-zinc-400 hover:text-white transition-colors"
                aria-label="Close value panel"
                data-testid="vault-value-panel-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <CollectionValueDashboard />
            </div>
          </aside>
        </div>
      )}

      {/* Command Palette */}
      <CommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        onAction={handleCommandPaletteAction}
        hasSelection={selectedItems.length > 0}
        selectionCount={selectedItems.length}
      />
    </div>
  );
}
